import React from "react";
import Layout from "../central/Layout";
import Panel from "../central/Panel";
import { HeadingSmall, BodyText } from "../central/Typography";
import theme from "../central/theme";

const AppStatus = () => {
  const lastUpdated = new Date().toLocaleString(undefined, { month: "short", year: "numeric" });

  const styles = {
    pageWrap: {
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing.medium,
    },
    betaBanner: {
      background: "rgba(0,0,0,0.03)",
      border: "1px solid rgba(0,0,0,0.06)",
      padding: theme.spacing.medium,
    },
    bannerTitleRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: theme.spacing.small,
      marginBottom: theme.spacing.small,
    },
    pillRow: {
      display: "flex",
      flexWrap: "wrap",
      gap: 8,
      marginTop: theme.spacing.small,
    },
    pill: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "6px 10px",
      borderRadius: 999,
      fontSize: 12,
      border: "1px solid rgba(0,0,0,0.08)",
      background: "rgba(255,255,255,0.9)",
      lineHeight: "14px",
      whiteSpace: "nowrap",
    },
    pillLive: {
      background: "rgba(34,197,94,0.08)",
      border: "1px solid rgba(34,197,94,0.25)",
      color: "#166534",
    },
    pillProgress: {
      background: "rgba(59,130,246,0.08)",
      border: "1px solid rgba(59,130,246,0.25)",
      color: "#1e3a8a",
    },
    pillRoadmap: {
      background: "rgba(124,58,237,0.08)",
      border: "1px solid rgba(124,58,237,0.25)",
      color: "#4c1d95",
    },
    grid: {
      display: "grid",
      gridTemplateColumns: "1fr",
      gap: theme.spacing.medium,
    },
    card: {
      backgroundColor: theme.colors.surface,
      padding: theme.spacing.medium,
      border: "1px solid rgba(0,0,0,0.06)",
    },
    cardHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: theme.spacing.small,
      marginBottom: theme.spacing.small,
    },
    sectionTitle: {
      marginBottom: theme.spacing.small,
      fontWeight: "bold",
    },
    list: {
      margin: 0,
      paddingLeft: 14,
    },
    listItem: {
      marginBottom: 6,
    },
    subtle: {
      opacity: 0.75,
    },
  };

  return (
    <Layout>
      {/* Small internal style block just for basic responsiveness */}
      <style>{`
        @media (min-width: 900px) {
          .appStatusGrid {
            grid-template-columns: 1fr 1fr;
          }
        }
      `}</style>

      <div style={styles.pageWrap}>
        {/* Top banner */}
        <Panel style={styles.betaBanner}>
          <div style={styles.bannerTitleRow}>
            <HeadingSmall style={{ marginBottom: 0, fontWeight: "bold" }}>App Status</HeadingSmall>
            <BodyText style={{ marginBottom: 0, ...styles.subtle }}>Last updated: {lastUpdated}</BodyText>
          </div>

          <BodyText style={{ marginBottom: 0 }}>
            Clubweek is currently in private beta.
            <br />
            Things may change week to week.
          </BodyText>

          <div style={styles.pillRow}>
            <span style={{ ...styles.pill, ...styles.pillLive }}>LIVE</span>
            <span style={{ ...styles.pill, ...styles.pillProgress }}>IN PROGRESS</span>
            <span style={{ ...styles.pill, ...styles.pillRoadmap }}>ROADMAP</span>
          </div>
          <BodyText style={{ marginBottom: 0 }}>
            <br />
            Built by: Alex Shoff
          </BodyText>
        </Panel>

        {/* Cards */}
        <div className="appStatusGrid" style={styles.grid}>
          <Panel style={styles.card}>
            <div style={styles.cardHeader}>
              <HeadingSmall style={{ marginBottom: 0, fontWeight: "bold" }}>Live Features</HeadingSmall>
              <span style={{ ...styles.pill, ...styles.pillLive }}>LIVE</span>
            </div>

            <ul style={styles.list}>
              <li style={styles.listItem}>
                <BodyText style={{ marginBottom: 0 }}>League creation and joining</BodyText>
              </li>
              <li style={styles.listItem}>
                <BodyText style={{ marginBottom: 0 }}>
                  Squad selection, budget checks, free transfers weekly rollover
                </BodyText>
              </li>
              <li style={styles.listItem}>
                <BodyText style={{ marginBottom: 0 }}>Admin match input + tools dashboard</BodyText>
              </li>
              <li style={styles.listItem}>
                <BodyText style={{ marginBottom: 0 }}>Player pricing engine</BodyText>
              </li>
              <li style={styles.listItem}>
                <BodyText style={{ marginBottom: 0 }}>Weekly points, statistics and leaderboard</BodyText>
              </li>
              <li style={styles.listItem}>
                <BodyText style={{ marginBottom: 0 }}>Player cards and transfer portal</BodyText>
              </li>
              <li style={styles.listItem}>
                <BodyText style={{ marginBottom: 0 }}>Claim player + view your stats</BodyText>
              </li>
              <li style={styles.listItem}>
                <BodyText style={{ marginBottom: 0 }}>Multi-league compatibility</BodyText>
              </li>
              <li style={styles.listItem}>
                <BodyText style={{ marginBottom: 0 }}>End and begin new season</BodyText>
              </li>
              <li style={styles.listItem}>
                <BodyText style={{ marginBottom: 0 }}>Dream Team</BodyText>
              </li>
              <li style={styles.listItem}>
                <BodyText style={{ marginBottom: 0 }}>Phone notifications and app</BodyText>
              </li>
            </ul>
          </Panel>

          <Panel style={styles.card}>
            <div style={styles.cardHeader}>
              <HeadingSmall style={{ marginBottom: 0, fontWeight: "bold" }}>Next up</HeadingSmall>
              <span style={{ ...styles.pill, ...styles.pillRoadmap }}>ROADMAP</span>
            </div>

            <ul style={styles.list}>
              <li style={styles.listItem}>
                <BodyText style={{ marginBottom: 0 }}>Availability tool</BodyText>
              </li>
              <li style={styles.listItem}>
                <BodyText style={{ marginBottom: 0 }}>Match + availability reminders</BodyText>
              </li>
              <li style={styles.listItem}>
                <BodyText style={{ marginBottom: 0 }}>View other people's teams</BodyText>
              </li>
              <li style={{ ...styles.listItem, marginBottom: 0 }}>
                <BodyText style={{ marginBottom: 0 }}>League organisation features (longer-term)</BodyText>
              </li>
            </ul>
          </Panel>


          <Panel style={styles.card}>
            <div style={styles.cardHeader}>
              <HeadingSmall style={{ marginBottom: 0, fontWeight: "bold" }}>
                Continued improvements
              </HeadingSmall>
              <span style={{ ...styles.pill, ...styles.pillProgress }}>
                IN PROGRESS
              </span>
            </div>

            <BodyText style={{ marginBottom: 0, ...styles.subtle }}>
              Ongoing: visual design polish, performance improvements, and UX refinements throughout the app.
              <br />
              If you spot something that could be clearer or smoother, let us know via Help &amp; Support.
            </BodyText>
          </Panel>
        </div>
      </div>
    </Layout>
  );
};

export default AppStatus;
